/**
 * DTC Data Service
 * React Query hooks for fetching DTC (Direct-to-Consumer) data from Shopify/Amazon platforms
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './api-client'
import { QueryKeys, getInvalidators } from './query-cache'

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface DtcPlatform {
  id: string
  platform_type: 'shopify' | 'amazon_seller_central'
  platform_name: string
  is_active: boolean
  last_sync_at: string | null
  sync_status: 'pending' | 'syncing' | 'success' | 'error'
  sync_error_message: string | null
  created_at: string
  updated_at: string
}

export interface DtcKpiSummary {
  total_revenue: number
  total_orders: number
  avg_order_value: number
  total_units: number
  new_customer_revenue: number
  new_customer_orders: number
  returning_customer_revenue: number
  returning_customer_orders: number
  discount_total: number
  net_revenue: number
  yoy_growth: number | null
}

export interface DtcTrendDataPoint {
  date: string
  total_revenue: number
  total_orders: number
  avg_order_value: number
  total_units: number
}

export interface DtcPlatformBreakdown {
  platform_id: string
  platform_name: string
  platform_type: string
  revenue: number
  orders: number
  units: number
  avg_order_value: number
}

export interface DtcKpiResponse {
  summary: DtcKpiSummary
  by_platform?: DtcPlatformBreakdown[] // Per-platform breakdown when aggregating
  trend: DtcTrendDataPoint[]
  date_range: {
    start: string
    end: string
    days: number
  }
}

export interface DtcProduct {
  product_id: string
  product_title: string
  sku: string | null
  revenue: number
  units_sold: number
  orders: number
  returns: number
  avg_price: number
  inventory: number
}

export interface DtcProductsResponse {
  products: DtcProduct[]
  pagination: {
    page: number
    limit: number
    total: number
    total_pages: number
  }
  summary: {
    total_revenue: number
    total_units: number
    total_products: number
  }
  date_range: {
    start: string
    end: string
  }
}

export interface DtcCustomerSummary {
  total_customers: number
  new_customers: number
  returning_customers: number
  avg_lifetime_value: number
  avg_conversion_rate: number
  avg_cart_abandonment_rate: number
  avg_customer_acquisition_cost: number
}

export interface DtcCustomerTrend {
  date: string
  new_customers: number
  returning_customers: number
  total_customers: number
  avg_lifetime_value: number
  conversion_rate: number
}

export interface DtcCohort {
  cohort_month: string
  cohort_size: number
  cohort_ltv: number
}

export interface DtcCustomersResponse {
  summary: DtcCustomerSummary
  trend: DtcCustomerTrend[]
  cohorts: DtcCohort[]
  date_range: {
    start: string
    end: string
  }
}

export interface DtcSyncStatus {
  platform_id: string
  platform_type: string
  platform_name: string
  sync_status: string
  last_sync_at: string | null
  sync_error_message: string | null
  created_at: string
  data_coverage: {
    earliest_date: string | null
    latest_date: string | null
    total_days: number
    total_orders: number
    total_revenue: number
  }
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Fetch all DTC platforms for the current company
 */
export function useDtcPlatforms() {
  return useQuery({
    queryKey: ['dtc', 'platforms'],
    queryFn: () => apiClient.get<DtcPlatform[]>('/dtc/platforms'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true
  })
}

/**
 * Fetch DTC KPIs for a specific platform or all platforms
 * 
 * @param platformId - UUID of the DTC platform (null = aggregate all platforms)
 * @param date - Optional date (YYYY-MM-DD)
 * @param options - Additional options (days, view_type, fiscal_week)
 */
export function useDtcKpis(
  platformId: string | null,
  date?: string,
  options?: {
    days?: number
    view_type?: 'calendar' | 'fiscal'
    fiscal_week?: number
    start_date?: string
    end_date?: string
  }
) {
  return useQuery({
    queryKey: ['dtc', 'kpis', platformId, date, options],
    queryFn: () => {
      const params = new URLSearchParams()
      
      // If platformId is null, we aggregate all platforms
      if (platformId) {
        params.append('platform_id', platformId)
      }
      
      // NEW: Support start_date/end_date for direct date range
      if (options?.start_date && options?.end_date) {
        params.append('start_date', options.start_date)
        params.append('end_date', options.end_date)
      } else if (date) {
        // Fallback to old date + days approach
        params.append('date', date)
      }
      
      if (options?.days) {
        params.append('days', options.days.toString())
      }
      
      if (options?.view_type) {
        params.append('view_type', options.view_type)
      }
      
      if (options?.fiscal_week) {
        params.append('fiscal_week', options.fiscal_week.toString())
      }
      
      const url = `/dtc/kpis?${params.toString()}`
      console.log('[useDtcKpis] Fetching:', url, { platformId, date, options })
      return apiClient.get<DtcKpiResponse>(url)
    },
    enabled: true, // Always enabled - can aggregate all platforms
    staleTime: 60 * 1000, // 1 minute
    refetchOnMount: true
  })
}

/**
 * Fetch product performance for a DTC platform
 * 
 * @param platformId - UUID of the DTC platform
 * @param options - Filtering and pagination options
 */
export function useDtcProducts(
  platformId: string | null,
  options?: {
    date?: string
    days?: number
    sort?: 'revenue' | 'units_sold' | 'avg_price' | 'returns'
    sort_dir?: 'asc' | 'desc'
    page?: number
    limit?: number
  }
) {
  return useQuery({
    queryKey: ['dtc', 'products', platformId, options],
    queryFn: () => {
      const params = new URLSearchParams()
      
      if (!platformId) {
        throw new Error('Platform ID is required')
      }
      
      params.append('platform_id', platformId)
      
      if (options?.date) params.append('date', options.date)
      if (options?.days) params.append('days', options.days.toString())
      if (options?.sort) params.append('sort', options.sort)
      if (options?.sort_dir) params.append('sort_dir', options.sort_dir)
      if (options?.page) params.append('page', options.page.toString())
      if (options?.limit) params.append('limit', options.limit.toString())
      
      return apiClient.get<DtcProductsResponse>(`/dtc/products?${params.toString()}`)
    },
    enabled: !!platformId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnMount: true
  })
}

/**
 * Fetch customer analytics for a DTC platform
 * 
 * @param platformId - UUID of the DTC platform
 * @param dateRange - Optional date range
 */
export function useDtcCustomers(
  platformId: string | null,
  dateRange?: {
    start_date?: string
    end_date?: string
    days?: number
  }
) {
  return useQuery({
    queryKey: ['dtc', 'customers', platformId, dateRange],
    queryFn: () => {
      const params = new URLSearchParams()
      
      if (!platformId) {
        throw new Error('Platform ID is required')
      }
      
      params.append('platform_id', platformId)
      
      if (dateRange?.start_date) params.append('start_date', dateRange.start_date)
      if (dateRange?.end_date) params.append('end_date', dateRange.end_date)
      if (dateRange?.days) params.append('days', dateRange.days.toString())
      
      return apiClient.get<DtcCustomersResponse>(`/dtc/customers?${params.toString()}`)
    },
    enabled: !!platformId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnMount: true
  })
}

/**
 * Fetch sync status for a DTC platform
 * 
 * @param platformId - UUID of the DTC platform
 */
export function useDtcSyncStatus(platformId: string | null) {
  return useQuery({
    queryKey: ['dtc', 'sync-status', platformId],
    queryFn: () => {
      if (!platformId) {
        throw new Error('Platform ID is required')
      }
      
      return apiClient.get<DtcSyncStatus>(`/dtc/sync-status?platform_id=${platformId}`)
    },
    enabled: !!platformId,
    staleTime: 10 * 1000, // 10 seconds (more frequent for status updates)
    refetchInterval: (data) => {
      // Auto-refetch every 5 seconds if syncing
      return data?.sync_status === 'syncing' ? 5000 : false
    },
    refetchOnMount: true
  })
}

/**
 * Mutation: Trigger data sync for a DTC platform
 */
export function useTriggerDtcSync() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (params: { platform_id: string; full_sync?: boolean }) => {
      return apiClient.post<{ success: boolean; message: string }>('/dtc/trigger-sync', params)
    },
    onSuccess: (_, variables) => {
      // ✅ Use centralized cache invalidation (see lib/query-cache.ts)
      const invalidate = getInvalidators(queryClient)
      invalidate.dtc.afterSync(variables.platform_id)
    }
  })
}

/**
 * Mutation: Aggregate sales data from all platforms into dtc_sales_summary
 */
export function useAggregateDtcSales() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (params?: { days?: number; force?: boolean }) => {
      const queryParams = new URLSearchParams()
      if (params?.days) queryParams.append('days', params.days.toString())
      if (params?.force) queryParams.append('force', 'true')
      
      const url = `/dtc/aggregate-sales${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      return apiClient.post<{ 
        success: boolean
        message: string
        records_processed: { amazon: number }
        coverage_before: any[]
        coverage_after: any[]
      }>(url, {})
    },
    onSuccess: () => {
      // ✅ Use centralized cache invalidation
      const invalidate = getInvalidators(queryClient)
      invalidate.dtc.afterAggregation()
    }
  })
}

// ============================================================================
// Helper Hooks
// ============================================================================

/**
 * Get the currently selected DTC platform from platforms list
 * 
 * @param platforms - Array of DTC platforms
 * @param selectedPlatformId - ID of selected platform
 * @returns Selected platform object or null
 */
export function useSelectedDtcPlatform(
  platforms: DtcPlatform[] | undefined,
  selectedPlatformId: string | null
): DtcPlatform | null {
  if (!platforms || !selectedPlatformId) {
    return null
  }
  
  return platforms.find(p => p.id === selectedPlatformId) || null
}

/**
 * Check if company has any active DTC platforms
 * 
 * @param platforms - Array of DTC platforms
 * @returns True if at least one active platform exists
 */
export function useHasDtcPlatforms(platforms: DtcPlatform[] | undefined): boolean {
  return (platforms?.length || 0) > 0
}

/**
 * Get platforms grouped by type
 * 
 * @param platforms - Array of DTC platforms
 * @returns Object with platforms grouped by platform_type
 */
export function useDtcPlatformsByType(platforms: DtcPlatform[] | undefined): Record<string, DtcPlatform[]> {
  if (!platforms) {
    return {}
  }
  
  return platforms.reduce((acc, platform) => {
    const type = platform.platform_type
    if (!acc[type]) {
      acc[type] = []
    }
    acc[type].push(platform)
    return acc
  }, {} as Record<string, DtcPlatform[]>)
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format currency value
 */
export function formatDtcCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

/**
 * Format percentage value
 */
export function formatDtcPercent(value: number, decimals: number = 1): string {
  return `${(value * 100).toFixed(decimals)}%`
}

/**
 * Format number with commas
 */
export function formatDtcNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

/**
 * Calculate percentage change
 */
export function calculatePercentChange(current: number, previous: number): number | null {
  if (previous === 0) return null
  return (current - previous) / previous
}

/**
 * Get sync status badge color
 */
export function getSyncStatusColor(status: string): string {
  switch (status) {
    case 'success':
      return 'green'
    case 'syncing':
      return 'blue'
    case 'error':
      return 'red'
    case 'pending':
      return 'yellow'
    default:
      return 'gray'
  }
}

/**
 * Get platform type display name
 */
export function getPlatformTypeDisplay(type: string): string {
  switch (type) {
    case 'shopify':
      return 'Shopify'
    case 'amazon_seller_central':
      return 'Amazon Seller Central'
    default:
      return type
  }
}

// ============================================================================
// DTC INSIGHTS API
// ============================================================================

export interface DtcInsight {
  id: string
  platform_id?: string
  date_range_start: string
  date_range_end: string
  insight_type: string
  category: string
  priority: string
  title: string
  description: string
  recommendation: string
  dollar_impact: number | null
  confidence_score: number | null
  source: string
  platform?: {
    name: string
    type: string
  }
  related_sku?: string
  related_customer_id?: string
  created_at: string
}

export interface DtcInsightsResponse {
  insights: DtcInsight[]
  total: number
  filters: {
    platform_id: string
    start_date: string | null
    end_date: string | null
    category: string
    priority: string
    limit: number
  }
}

/**
 * Fetch DTC insights from the API
 */
export function useDtcInsights(
  platformId: string | null | undefined,
  options?: {
    startDate?: string
    endDate?: string
    category?: string
    priority?: string
    limit?: number
  }
) {
  return useQuery({
    queryKey: ['dtc-insights', platformId, options],
    queryFn: async () => {
      const params = new URLSearchParams()
      
      if (platformId && platformId !== 'all') {
        params.append('platform_id', platformId)
      }
      
      if (options?.startDate) {
        params.append('start_date', options.startDate)
      }
      
      if (options?.endDate) {
        params.append('end_date', options.endDate)
      }
      
      if (options?.category) {
        params.append('category', options.category)
      }
      
      if (options?.priority) {
        params.append('priority', options.priority)
      }
      
      if (options?.limit) {
        params.append('limit', options.limit.toString())
      }
      
      const response = await apiClient.get<DtcInsightsResponse>(`/insights/dtc-list?${params.toString()}`)
      return response
    },
    enabled: !!(options?.startDate && options?.endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Generate DTC insights
 */
export function useGenerateDtcInsights() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (params: {
      startDate: string
      endDate: string
      platformId?: string | null
      force?: boolean
    }) => {
      const response = await apiClient.post<{
        success: boolean
        insightsGenerated: number
        message: string
      }>('/insights/dtc-generate', {
        startDate: params.startDate,
        endDate: params.endDate,
        platformId: params.platformId === 'all' ? null : params.platformId,
        force: params.force || false
      })
      return response
    },
    onSuccess: () => {
      // ✅ Use centralized cache invalidation
      const invalidate = getInvalidators(queryClient)
      invalidate.insights.all()
    }
  })
}

